/**
 * O menu lateral (#880): o trilho, com as áreas, a gestão, as configurações e o
 * rodapé (feedback, suporte, perfil, trocar de organização, sair), e o véu da
 * gaveta do celular.
 *
 * Ele não é dono de estado de tela nenhum: a área aberta e as seções vêm da
 * casca, porque a escolha da tela, a rota e a barra do celular também as leem.
 * Ele só avisa o que a pessoa escolheu.
 */

import { useState, type ReactNode } from 'react';
import type { Me } from '@cliqcrm/contracts';
import type { SecaoDaAdministracao } from '../config/secoes-da-administracao';
import { geraSuperAdmins, nomeDoPapel, podeCriarOrganizacaoComo } from '@/entities/acesso/papel';
import { MeuPerfil } from '@/features/acesso/trocar-foto';
import { BotaoDeFeedback } from '@/features/acesso/enviar-feedback';
import { useTema } from '@/shared/lib/tema';
import { podeNaSessao } from '@/shared/sessao/contexto';
import { Icone } from '@/shared/ui';
import { FotoOuIniciais } from '@/shared/ui/foto-ou-iniciais/foto-ou-iniciais';
import {
  AREAS,
  GESTAO,
  LEITURA_DA_AREA,
  RELATORIOS,
  type Area,
  type SecaoDosRelatorios,
} from '../config/areas';
import { AREAS_DA_GESTAO, automacoesLiberadas, mostraSubmenuDeConfiguracoes } from '../lib/menu';
import type { Trilho } from '../model/usar-trilho';
import { BalaoDeSuporte } from './balao-de-suporte';
import { ItemDoMenu } from './item-do-menu';
import { Marca } from './marca';

export interface PropsDoMenuLateral {
  readonly trilho: Trilho;
  readonly me: Me | null;
  readonly area: Area;
  readonly aoAbrirArea: (area: Area) => void;
  /** O painel abre sem o canal que outra navegação deixou. */
  readonly aoAbrirPainel: () => void;
  readonly secaoDosRelatorios: SecaoDosRelatorios;
  readonly aoEscolherRelatorio: (secao: SecaoDosRelatorios) => void;
  readonly secaoDaAdmin: SecaoDaAdministracao;
  readonly aoEscolherSecaoDaAdmin: (secao: SecaoDaAdministracao) => void;
  /** Qual tela das automações está aberta, para o submenu marcar. */
  readonly secaoDasAutomacoes?:
    'lista' | 'acompanhamento' | 'listas' | 'integracoes' | 'operacoes' | null;
  readonly aoEscolherSecaoDasAutomacoes?: (
    secao: 'lista' | 'acompanhamento' | 'listas' | 'integracoes' | 'operacoes',
  ) => void;
  /** As seções de Configurações que esta pessoa vê. */
  readonly secoesDestaPessoa: readonly {
    chave: SecaoDaAdministracao;
    nome: string;
    apoio?: string;
  }[];
  readonly aviso: { readonly mensagens: number; readonly conversas: number };
  /**
   * O sino e o "@" das menções, desenhados pela casca (o widget das
   * notificações). O menu só dá o lugar, ao lado do Atendimento.
   */
  readonly avisos: ReactNode;
  readonly podeTrocarDeRevenda: boolean;
  readonly aoTrocarDeRevenda: () => void;
  readonly aoSair: () => void;
  /** A tela que vai junto com o feedback: o caminho (#806) e o nome que a pessoa vê. */
  readonly telaDoFeedback: { readonly caminho: string; readonly nome: string };
}

export function MenuLateral({
  trilho: estadoDoTrilho,
  me,
  area,
  aoAbrirArea,
  aoAbrirPainel,
  secaoDosRelatorios,
  aoEscolherRelatorio,
  secaoDaAdmin,
  aoEscolherSecaoDaAdmin,
  secaoDasAutomacoes = null,
  aoEscolherSecaoDasAutomacoes,
  secoesDestaPessoa,
  aviso,
  avisos,
  podeTrocarDeRevenda,
  aoTrocarDeRevenda,
  aoSair,
  telaDoFeedback,
}: PropsDoMenuLateral): ReactNode {
  const {
    trilho,
    estreita,
    gavetaAberta,
    definirGavetaAberta,
    aberta,
    recolhido,
    alternarRecolhido,
    definirEspiando,
    espiarPausado,
    definirEspiarPausado,
    mostrandoRecolhido,
  } = estadoDoTrilho;
  const [perfilAberto, definirPerfilAberto] = useState(false);
  const tema = useTema();
  const podeCriarOrganizacao = podeCriarOrganizacaoComo(me);
  /** A área aparece para quem tem a leitura dela (#732); sem regra, para todos. */
  const ve = (chave: string): boolean => {
    const leitura = LEITURA_DA_AREA[chave];
    return leitura === undefined || podeNaSessao(me, leitura.recurso, leitura.acao);
  };

  /*
   * O submenu que a pessoa recolheu pela seta. Ele abre sozinho ao entrar na
   * área; clicar de novo no item aberto o recolhe e o reabre. Vale só para a
   * área em que foi recolhido: saindo e voltando, ele abre de novo.
   */
  const [recolhidoPelaSeta, definirRecolhidoPelaSeta] = useState<{
    chave: string;
    area: Area;
  } | null>(null);
  const submenuAberto = (chave: string, naArea: boolean): boolean =>
    naArea && !(recolhidoPelaSeta?.chave === chave && recolhidoPelaSeta.area === area);
  /** Abre a área, ou, já nela, recolhe e reabre o submenu. */
  const abrirOuAlternar = (chave: string, naArea: boolean, abrir: () => void) => () => {
    if (!naArea) {
      definirRecolhidoPelaSeta(null);
      abrir();
      return;
    }
    definirRecolhidoPelaSeta(submenuAberto(chave, true) ? { chave, area } : null);
  };
  const naAreaDoFunil = area === 'funil' || area === 'etapas-do-funil';
  /*
   * Comercial só AGRUPA (28/10), como Automações: o item abre e fecha o
   * submenu, e o quadro é "Funil", o subitem. As etapas são um botão na tela do
   * Funil. Clicar no item com as etapas abertas recolhia o submenu em vez de
   * voltar ao quadro — com o Funil como subitem, a volta é ele.
   */
  const [comercialAberto, definirComercialAberto] = useState<boolean | null>(null);
  const submenuDoComercial = comercialAberto ?? naAreaDoFunil;
  const comSubmenuDasAutomacoes =
    automacoesLiberadas(me) && aoEscolherSecaoDasAutomacoes !== undefined;
  /*
   * Automações só AGRUPA (28/10): o item abre e fecha o submenu, e a lista é
   * "Minhas automações", o primeiro subitem. Sem escolha da pessoa, o submenu
   * fica aberto enquanto ela está numa tela das automações.
   */
  const [automacoesAbertas, definirAutomacoesAbertas] = useState<boolean | null>(null);
  const submenuDasAutomacoes = automacoesAbertas ?? area === 'automacoes';

  return (
    <>
      <nav
        ref={trilho}
        id="menu-principal"
        className="casca__lateral"
        aria-label="Navegação principal"
        inert={estreita && !gavetaAberta ? true : undefined}
        /*
         * O foco abre junto com o ponteiro.
         *
         * Quem navega por teclado chega nos itens por Tab e não tem mouse para
         * revelar os rótulos. `onFocus`/`onBlur` no React sobem dos filhos —
         * é o `focusin`/`focusout` do DOM —, então entrar em qualquer item
         * abre, e sair do último fecha.
         */
        onMouseEnter={() => {
          if (!espiarPausado) definirEspiando(true);
        }}
        onMouseLeave={() => {
          definirEspiando(false);
          definirEspiarPausado(false);
        }}
        onFocus={() => {
          if (!espiarPausado) definirEspiando(true);
        }}
        onBlur={(evento) => {
          // Só fecha quando o foco sai do menu INTEIRO: `relatedTarget` é para
          // onde ele foi, e um item vizinho ainda está aqui dentro.
          if (!evento.currentTarget.contains(evento.relatedTarget)) definirEspiando(false);
        }}
      >
        {/*
        A marca e o controle do trilho na MESMA linha.

        O botão estava sozinho abaixo, e um controle solto num canto vazio
        parece sobra de layout. Ao lado da marca ele vira parte do cabeçalho
        do trilho — é onde a pessoa já olha ao entrar.
      */}
        <div className="casca__topo">
          {/*
          Recolhido, o controle SAI e quem expande é a marca.
          O botão de recolher some do trilho a pedido do dono do produto — e
          um trilho sem nenhuma volta seria uma armadilha, então o símbolo
          passa a ser o botão. É o gesto que o mercado ensinou: clicar na
          marca do canto abre o menu.
        */}
          {mostrandoRecolhido ? (
            <button
              className="casca__marca casca__marca--acao"
              type="button"
              aria-expanded={false}
              aria-label="Expandir o menu"
              title="Expandir o menu"
              onClick={() => alternarRecolhido(false)}
            >
              <img className="casca__simbolo" src="/marca/simbolo-cliqcrm-claro.png" alt="" />
            </button>
          ) : (
            <Marca />
          )}
          {/*
          Só existe quando há o que recolher.
          Recolhido, ele sai do trilho a pedido do dono do produto — e sai do
          DOM, não por CSS: escondido, continuaria na árvore de acessibilidade
          com o mesmo nome do botão da marca, que é quem expande dali em
          diante.
        */}
          {!mostrandoRecolhido && (
            <button
              className="casca__recolher"
              type="button"
              /*
              O botão FIXA o estado, e por isso ele fala do estado guardado —
              não do que se está vendo. Espiando, o trilho parece aberto mas
              continua recolhido: aqui ele diz "Expandir", e clicar prende o
              menu aberto. Era isto que estava quebrado — o clique mandava
              recolher o que já estava recolhido, e não havia volta.
            */
              aria-expanded={!recolhido}
              aria-label={recolhido ? 'Expandir o menu' : 'Recolher o menu'}
              onClick={() => alternarRecolhido(!recolhido)}
            >
              <Icone nome="recolher" />
            </button>
          )}
        </div>

        {/* Só a LISTA rola. Antes o trilho inteiro rolava, e com a janela baixa
          o nome de quem está logado e o Sair saíam da tela. */}
        <div className="casca__lista">
          {AREAS.filter((item) => ve(item.chave)).map((item) => (
            <div
              key={item.chave}
              className={
                item.chave === 'atendimento' ? 'casca__linha casca__linha--sino' : 'casca__linha'
              }
            >
              <ItemDoMenu
                nome={item.nome}
                icone={<Icone nome={item.icone} />}
                recolhido={mostrandoRecolhido}
                /*
                 * A bolinha no ícone, só com o menu fechado e só no Atendimento.
                 *
                 * Aberto, quem avisa é o sino ao lado, com o número. Mostrar os
                 * dois seria dizer a mesma coisa duas vezes na mesma linha.
                 */
                aviso={item.chave === 'atendimento' && mostrandoRecolhido && aviso.mensagens > 0}
                emBreve={item.chave === 'automacoes' && !automacoesLiberadas(me)}
                {...(item.chave === 'funil'
                  ? { submenu: submenuDoComercial ? 'aberto' : 'fechado' }
                  : item.chave === 'automacoes' && comSubmenuDasAutomacoes
                    ? { submenu: submenuDasAutomacoes ? 'aberto' : 'fechado' }
                    : {})}
                // As etapas são do Funil: com elas abertas, o Funil continua aceso.
                ativo={
                  area === item.chave || (item.chave === 'funil' && area === 'etapas-do-funil')
                }
                /*
                 * Abre quem TEM tela. O item sem `aoAbrir` fica visível e inerte —
                 * é o que sustenta o selo "em breve": a área aparece no menu, e
                 * clicar nela não leva a lugar nenhum de propósito.
                 *
                 * `clientes` entrou aqui junto com a tela. Esquecer esta linha
                 * teria deixado o item sem selo E sem destino, que é pior que
                 * qualquer um dos dois.
                 */
                aoAbrir={
                  item.chave === 'funil'
                    ? // Recolhido, não há submenu à vista: o ícone leva ao Funil.
                      mostrandoRecolhido
                      ? () => aoAbrirArea('funil')
                      : () => definirComercialAberto(!submenuDoComercial)
                    : item.chave === 'automacoes' && comSubmenuDasAutomacoes
                      ? // Recolhido, não há submenu à vista: o ícone leva às Minhas automações.
                        mostrandoRecolhido
                        ? () => aoEscolherSecaoDasAutomacoes?.('lista')
                        : () => definirAutomacoesAbertas(!submenuDasAutomacoes)
                      : item.chave === 'atendimento' ||
                          item.chave === 'inbox' ||
                          item.chave === 'clientes' ||
                          item.chave === 'tarefas' ||
                          item.chave === 'ia' ||
                          item.chave === 'automacoes'
                        ? () => aoAbrirArea(item.chave)
                        : undefined
                }
              />
              {/*
              O sino fica ao lado do Atendimento porque é ali que a mensagem
              é respondida; um sino no topo, longe do trabalho, se perderia.
              O sino e o "@" das menções (#569) moram juntos no canto da
              linha. O sino liga e desliga o som; o "@" abre quem chamou
              esta pessoa numa nota interna. Cada um se desenha sozinho
              quando não há nada: ver `sino.tsx` e `mencoes-para-mim.tsx`.
            */}
              {/*
              O submenu do Funil: o quadro é o próprio Funil, e as etapas
              (que eram de Configurações) moram aqui, onde se mexe no quadro.
            */}
              {item.chave === 'funil' && submenuDoComercial && !mostrandoRecolhido && (
                <ul className="casca__submenu" aria-label="Comercial">
                  <li>
                    <button
                      type="button"
                      className="casca__subitem"
                      // As etapas abrem pelo botão do Funil: com elas abertas, o Funil continua aceso.
                      aria-current={naAreaDoFunil ? 'page' : undefined}
                      onClick={() => aoAbrirArea('funil')}
                    >
                      Funil
                    </button>
                  </li>
                </ul>
              )}
              {/*
              O submenu das Automações: cada item entra com a sua tela.
            */}
              {item.chave === 'automacoes' &&
                submenuDasAutomacoes &&
                comSubmenuDasAutomacoes &&
                aoEscolherSecaoDasAutomacoes &&
                !mostrandoRecolhido && (
                  <ul className="casca__submenu" aria-label="Automações">
                    <li>
                      <button
                        type="button"
                        className="casca__subitem"
                        aria-current={secaoDasAutomacoes === 'lista' ? 'page' : undefined}
                        onClick={() => aoEscolherSecaoDasAutomacoes('lista')}
                      >
                        Minhas automações
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="casca__subitem"
                        aria-current={secaoDasAutomacoes === 'acompanhamento' ? 'page' : undefined}
                        onClick={() => aoEscolherSecaoDasAutomacoes('acompanhamento')}
                      >
                        Acompanhamento
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="casca__subitem"
                        aria-current={secaoDasAutomacoes === 'listas' ? 'page' : undefined}
                        onClick={() => aoEscolherSecaoDasAutomacoes('listas')}
                      >
                        Listas
                      </button>
                    </li>
                    {/* Só da Cliqfy: o banco do cliente não é assunto da empresa. */}
                    {podeNaSessao(me, 'integracao_de_dados', 'ler') && (
                      <li>
                        <button
                          type="button"
                          className="casca__subitem"
                          aria-current={secaoDasAutomacoes === 'integracoes' ? 'page' : undefined}
                          onClick={() => aoEscolherSecaoDasAutomacoes('integracoes')}
                        >
                          Integrações
                        </button>
                      </li>
                    )}
                    {podeNaSessao(me, 'integracao_de_dados', 'ler') && (
                      <li>
                        <button
                          type="button"
                          className="casca__subitem"
                          aria-current={secaoDasAutomacoes === 'operacoes' ? 'page' : undefined}
                          onClick={() => aoEscolherSecaoDasAutomacoes('operacoes')}
                        >
                          Operações SQL
                        </button>
                      </li>
                    )}
                  </ul>
                )}
              {item.chave === 'atendimento' && !mostrandoRecolhido && (
                <div className="casca__avisos">{avisos}</div>
              )}
            </div>
          ))}

          <div className="casca__separador" />
          <div className="casca__rotulo">Gestão</div>

          {/* Configurações fica de fora daqui: é sempre o ÚLTIMO item (logo abaixo). */}
          {GESTAO.filter((item) => item.chave !== 'configuracoes' && ve(item.chave)).map((item) => (
            <div key={item.chave}>
              <ItemDoMenu
                nome={item.nome}
                icone={<Icone nome={item.icone} />}
                recolhido={mostrandoRecolhido}
                ativo={area === item.chave}
                {...(item.chave === 'painel'
                  ? { submenu: submenuAberto('painel', area === 'painel') ? 'aberto' : 'fechado' }
                  : {})}
                aoAbrir={
                  item.chave === 'painel'
                    ? abrirOuAlternar('painel', area === 'painel', aoAbrirPainel)
                    : () => aoAbrirArea('identidade')
                }
              />
              {/* O submenu dos relatórios, com a mesma regra do de Configurações. */}
              {item.chave === 'painel' &&
                submenuAberto('painel', area === 'painel') &&
                !mostrandoRecolhido && (
                  <ul className="casca__submenu" aria-label="Relatórios">
                    {RELATORIOS.map((r) => (
                      <li key={r.chave}>
                        <button
                          type="button"
                          className="casca__subitem"
                          aria-current={secaoDosRelatorios === r.chave ? 'page' : undefined}
                          onClick={() => aoEscolherRelatorio(r.chave)}
                        >
                          {r.nome}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          ))}

          {/*
          Feedback (#561): só a organização de suporte da Cliqfy lê o que as
          outras mandam pelo botão do canto da tela. Para as demais o item
          nem existe, e a API recusa a lista.
        */}
          {me?.suporte && (
            <ItemDoMenu
              nome="Feedback"
              icone={<Icone nome="nota" />}
              recolhido={mostrandoRecolhido}
              ativo={area === 'feedbacks'}
              aoAbrir={() => aoAbrirArea('feedbacks')}
            />
          )}

          {/*
          A gestão de organizações: só para o Super Admin (emendas de 21/10 e
          26/10 ao ADR-0025). A API confere o mesmo (provisionamento/permissao.ts).
          Criar uma organização e o catálogo de planos ficam dentro dela (#767).
        */}
          {podeCriarOrganizacao && (
            <ItemDoMenu
              nome="Organizações"
              icone={<Icone nome="predio" />}
              recolhido={mostrandoRecolhido}
              ativo={AREAS_DA_GESTAO.has(area)}
              aoAbrir={() => aoAbrirArea('organizacoes')}
            />
          )}

          {/* Só para o Super Admin: quem tem o papel, e dar ou tirar (ADR-0025). */}
          {geraSuperAdmins(me?.papel) && (
            <ItemDoMenu
              nome="Super Admins"
              icone={<Icone nome="pessoa" />}
              recolhido={mostrandoRecolhido}
              ativo={area === 'super-admins'}
              aoAbrir={() => aoAbrirArea('super-admins')}
            />
          )}

          {/*
          Configurações por último, sempre: os itens que só algumas
          organizações têm (Feedback, Nova organização) entram acima dele, e
          o submenu dele logo abaixo.
        */}
          <ItemDoMenu
            nome="Configurações"
            icone={<Icone nome="configuracoes" />}
            recolhido={mostrandoRecolhido}
            ativo={area === 'configuracoes'}
            submenu={
              submenuAberto('configuracoes', area === 'configuracoes') ? 'aberto' : 'fechado'
            }
            aoAbrir={abrirOuAlternar('configuracoes', area === 'configuracoes', () =>
              aoAbrirArea('configuracoes'),
            )}
          />

          {/*
        O submenu da administração.

        ── Por que só quando a área está aberta ─────────────────────────
        Quatro subitens sempre visíveis empurrariam "Início" e o rodapé para
        baixo em toda tela do produto, para servir a uma área que a pessoa
        visita de vez em quando. Abrir ao entrar é o que todo menu lateral
        faz, e é o que o dedo já espera.

        ── E por que some com o trilho ESTREITO ─────────────────────────
        Estreito, o trilho tem 64px e mostra só ícone. Um subitem sem texto e
        sem ícone próprio seria um retângulo mudo — e dar um ícone a cada
        seção só para caber aqui inventaria oito desenhos que ninguém pediu.

        A condição é `mostrandoRecolhido`, e NÃO `recolhido`: são coisas
        diferentes, e confundi-las foi o defeito. Com `recolhido`, o submenu
        sumia também enquanto o trilho estava aberto por espiar — a pessoa
        passava o mouse, via os nomes de todas as áreas voltarem, e
        Configurações continuava sem as seções. Ficava sem caminho nenhum
        para trocar de seção sem reabrir o menu.
      */}
          {mostraSubmenuDeConfiguracoes(area, mostrandoRecolhido) &&
            submenuAberto('configuracoes', true) && (
              <ul className="casca__submenu" aria-label="Seções das configurações">
                {secoesDestaPessoa.map((s) => (
                  <li key={s.chave}>
                    <button
                      type="button"
                      className="casca__subitem"
                      aria-current={secaoDaAdmin === s.chave ? 'page' : undefined}
                      onClick={() => aoEscolherSecaoDaAdmin(s.chave)}
                      title={s.apoio}
                    >
                      {s.nome}
                    </button>
                  </li>
                ))}
              </ul>
            )}

          {/*
          "Início" saiu do menu.

          Ela é o mostruário dos estados da interface — feita para quem
          CONSTRÓI o produto conferir vazio, erro e carregando lado a lado.
          Para quem atende, é uma porta que não leva a trabalho nenhum, e num
          menu de oito itens cada porta dessas custa atenção.

          A tela continua no código e o `area` continua aceitando `'inicio'`:
          devolver o item aqui é uma linha. Apagar a tela seria outra decisão,
          e não é esta.
        */}
        </div>

        <div className="casca__rodape">
          {/*
          O tema da tela, claro ou escuro (28/10). Um interruptor: ligado é o
          escuro. A escolha fica neste navegador; sem ela, vale o do sistema.
        */}
          <button
            type="button"
            className="casca__tema"
            role="switch"
            aria-checked={tema.escuro}
            aria-label="Modo escuro"
            onClick={tema.alternar}
            {...(mostrandoRecolhido
              ? { title: tema.escuro ? 'Mudar para o modo claro' : 'Mudar para o modo escuro' }
              : {})}
          >
            <span className="casca__icone" aria-hidden="true">
              <Icone nome={tema.escuro ? 'lua' : 'sol'} />
            </span>
            <span className="casca__sair-rotulo">Modo escuro</span>
            <span className="casca__chave" aria-hidden="true" />
          </button>
          {/*
          O feedback, em qualquer tela (#561). A tela vai junto: o caminho dela
          (#806) e o nome que a pessoa vê.
        */}
          {me && (
            <BotaoDeFeedback
              tela={telaDoFeedback}
              pessoa={me.usuario}
              recolhido={mostrandoRecolhido}
            />
          )}
          {/* O chat com o suporte da Cliqfy (29/09/2026), quando ligado no
            servidor. */}
          <BalaoDeSuporte recolhido={mostrandoRecolhido} />
          {me && (
            /*
            Era só leitura. Virou a porta de "Meu perfil" (061), onde se
            troca a foto — o lugar em que a pessoa já procura a si mesma.
          */
            <button
              type="button"
              className="casca__usuario"
              onClick={() => definirPerfilAberto(true)}
              aria-label={`Meu perfil: ${me.usuario.nome}`}
              {...(mostrandoRecolhido ? { title: 'Meu perfil' } : {})}
            >
              <FotoOuIniciais
                nome={me.usuario.nome}
                foto={me.usuario.foto_url}
                tamanho={30}
                classe="casca__avatar"
              />
              <span className="casca__quem">
                <b>{me.usuario.nome}</b>
                <span>
                  {me.tenant.nome} · {nomeDoPapel(me.papel)}
                </span>
              </span>
            </button>
          )}
          {perfilAberto && <MeuPerfil aoFechar={() => definirPerfilAberto(false)} />}
          {podeTrocarDeRevenda && (
            <button
              className="casca__sair"
              type="button"
              onClick={aoTrocarDeRevenda}
              {...(mostrandoRecolhido ? { title: 'Trocar de organização' } : {})}
            >
              <span className="casca__icone" aria-hidden="true">
                <Icone nome="trocar" />
              </span>
              <span className="casca__sair-rotulo">Trocar de organização</span>
            </button>
          )}
          <button
            className="casca__sair"
            type="button"
            onClick={aoSair}
            {...(mostrandoRecolhido ? { title: 'Sair' } : {})}
          >
            <span className="casca__icone" aria-hidden="true">
              <Icone nome="sair" />
            </span>
            {/* Classe própria, e não a tag: `.casca__icone` também é um span, e
              mirar a tag escondia o ícone junto com o rótulo — o botão virava
              um alvo vazio no trilho recolhido. */}
            <span className="casca__sair-rotulo">Sair</span>
          </button>
        </div>
      </nav>

      {aberta && (
        <button
          type="button"
          className="casca__veu"
          aria-label="Fechar o menu"
          onClick={() => definirGavetaAberta(false)}
        />
      )}
    </>
  );
}
